import { GitBranch } from '../models/git-worker.model';
import { WorkerAction, WorkerActionError, WorkerFnParams } from '../wasm-git.worker.model';
import type { GitAction } from './actions.enum';
import { FSReadDirActionFn } from './fs-read-dir';
import { FSReadFileActionFn } from './fs-read-file';
import { GitShowActionFn } from './git-show';

export interface GitBranchListAction extends WorkerAction {
  type: GitAction.BranchList;
}

export class GitBranchListActionError extends WorkerActionError {}

export async function GitBranchListActionFn(params: WorkerFnParams<GitBranchListAction>): Promise<GitBranch[]> {
  try {
    const { tree: refs } = await FSReadDirActionFn({ ...params, params: { path: './.git/refs' } });

    const heads = refs['heads']?.children;
    const remotes = refs['remotes']?.children?.['origin']?.children;

    const localBranches = Object.keys(heads || {});
    const remoteBranches = Object.keys(remotes || {});

    const defaultBranchName = await getDefaultBranch(params);

    return await Promise.all(
      Array.from(new Set([...localBranches, ...remoteBranches]))
        .filter((name) => name !== 'HEAD')
        .map(async (name) => {
          const localOnly = !remoteBranches.includes(name);
          const committerDate = await getBranchCommitterDate(
            params,
            `refs/${localOnly ? 'heads' : 'remotes/origin'}/${name}`,
          );
          return {
            name,
            local: localBranches.includes(name),
            published: !localOnly,
            committerDate,
            tree: {},
            default: name === defaultBranchName,
          };
        }),
    );
  } catch (err) {
    throw new GitBranchListActionError(err);
  }
}

async function getDefaultBranch(params: WorkerFnParams<WorkerAction>): Promise<string | undefined> {
  const gitConfig = await FSReadFileActionFn({ ...params, params: { path: './.git/config' } });
  // The default branch is the first tracked branch with properties
  // We need to check for the remote part because when a branch is renamed, the old name still appears
  // without properties
  const defaultBranchRegex = /\[branch "(.*)"\]\s+remote/;
  const results = defaultBranchRegex.exec(gitConfig);
  return results?.[1];
}

async function getBranchCommitterDate(params: WorkerFnParams<WorkerAction>, branchRef: string): Promise<Date> {
  const branchInfo = await GitShowActionFn({ ...params, params: { branch: branchRef } });

  /**
   * Committer Date in unix timestamp format is followed by committer email
   *
   * tree d1e3886be7964484913a7ee1bd3f9643e4fccfc1
   * parent bb2272588a32ec8b9ed10ce7081c43c786c39e8c
   * author FirstName LastName <abc@email.com> 1637770262 +0300
   * committer GitHub <noreply@github.com> 1637770262 +0300
   *                                       ^^^^^^^^^^
   * Update environment.json
   */
  const results = /committer.+<.+> (.+) /.exec(branchInfo);
  const unixTimestamp = Number(results?.[1] ?? 0);

  return new Date(unixTimestamp * 1000);
}
